import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from "@mui/material";
import {
  CalendarToday,
  Scale,
  Timeline,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";

export interface LotNutritionLaunch {
  data: string;
  alimento: string;
  quantidade_total_kg: number;
}

export interface LotNutritionData {
  _id: string;
  _key: string;
  nome_do_lote: string;
  total_de_animais: number;
  animais: string[];
  lancamentos_do_lote: LotNutritionLaunch[];
}

export interface LotNutritionHistoryData {
  data: LotNutritionData;
  resumo: string;
  url: string;
}

export interface LotNutritionHistoryViewProps {
  lotNutritionData: LotNutritionHistoryData;
}

const formatDate = (dateString?: string, t?: (key: string) => string): string => {
  if (!dateString) return t ? t('common:date_not_available') : "Data não disponível";
  try {
    // Se a data está no formato YYYY-MM-DD, adiciona horário para evitar problemas de timezone
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    // Para outros formatos, usa a conversão normal
    return new Date(dateString).toLocaleDateString("pt-BR");
  } catch {
    return dateString;
  }
};

export const LotNutritionHistoryView: React.FC<
  LotNutritionHistoryViewProps
> = ({ lotNutritionData }) => {
  const { t } = useTranslation(['farm', 'common']);
  const { data } = lotNutritionData;

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Extrair informações do lote
  const lotName = data?.nome_do_lote || 'N/A';
  const totalAnimals = data?.total_de_animais || 0;
  const animals = data?.animais || [];
  const launches = data?.lancamentos_do_lote || [];

  // Cálculos de paginação
  const totalPages = Math.ceil(launches.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = launches.slice(startIndex, endIndex);

  const handlePageClick = (page: number) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        minHeight: 0,
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#c1c1c1",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#a8a8a8",
        },
      }}
    >
      <Box sx={{ p: 4 }}>
        {/* Header com informações do lote */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <Chip
                label={t('farm:lotNutritionHistory.animalsCount', { count: totalAnimals })}
                size="small"
                sx={{
                  bgcolor: DESIGN_TOKENS.colors.neutral[100],
                  color: DESIGN_TOKENS.colors.text.primary,
                  fontFamily: "monospace",
                  fontWeight: 600,
                }}
              />
              <Chip
                label={t('farm:lotNutritionHistory.lot', { name: lotName })}
                size="small"
                color="secondary"
                variant="outlined"
              />
              <Chip
                label={t('farm:lotNutritionHistory.launchesCount', { count: launches.length })}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            {lotNutritionData.resumo && (
              <Typography
                variant="body2"
                sx={{
                  color: DESIGN_TOKENS.colors.text.secondary,
                  fontStyle: "italic",
                }}
              >
                {lotNutritionData.resumo}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Estatísticas */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t('farm:lotNutritionHistory.lotStatistics')}
            </Typography>
            <Stack direction="row" spacing={4} sx={{ flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img 
                  src="/vaca.png" 
                  alt="Animal" 
                  style={{ width: 24, height: 24, filter: 'brightness(0)' }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('farm:lotNutritionHistory.totalAnimals')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {totalAnimals}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Timeline
                  sx={{ color: DESIGN_TOKENS.colors.organization.farm }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('farm:lotNutritionHistory.feedingLaunches')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {launches.length}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarToday
                  sx={{ color: DESIGN_TOKENS.colors.organization.farm }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('farm:lotNutritionHistory.lastLaunch')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {launches.length > 0 ? formatDate(launches[0]?.data, t) : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Tags dos animais */}
        {animals.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Animais no Lote ({animals.length})
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {animals.map((animal, index) => (
                  <Chip
                    key={index}
                    label={animal}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Tabela de histórico */}
        {launches.length > 0 ? (
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Lançamentos de Alimentação ({launches.length})
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CalendarToday sx={{ fontSize: 18 }} />
                          {t('farm:lotNutritionHistory.date')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <img 
                            src="/forragem.png" 
                            alt="Forragem" 
                            style={{ width: 18, height: 18 }}
                          />
                          {t('farm:lotNutritionHistory.feedType')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Scale sx={{ fontSize: 18 }} />
                          {t('farm:lotNutritionHistory.quantity')}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentItems.map((launch, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          {formatDate(launch.data, t)}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              textTransform: "capitalize",
                            }}
                          >
                            {launch.alimento}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: DESIGN_TOKENS.colors.organization.farm,
                              }}
                            >
                              {launch.quantidade_total_kg} kg
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Paginação */}
              {totalPages > 1 && (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                         {t('common:pagination.pageOf', { current: currentPage, total: totalPages })} ({launches.length} {t('farm:lotNutritionHistory.records')})
                        </Typography>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<NavigateBefore />}
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                        >
                          {t('common:pagination.previous')}
                        </Button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                          (page) => (
                            <Button
                              key={page}
                              variant={page === currentPage ? "contained" : "outlined"}
                              size="small"
                              onClick={() => handlePageClick(page)}
                              sx={{ minWidth: 40 }}
                            >
                              {page}
                            </Button>
                          )
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          endIcon={<NavigateNext />}
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                        >
                          {t('common:pagination.next')}
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent>
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                }}
              >
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  {t('farm:lotNutritionHistory.noRecordsFound')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('farm:lotNutritionHistory.noFeedingRecords')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default LotNutritionHistoryView;
